import { Point } from '../../../core/models/point';
import { Figure } from './figure';
import * as jsts from 'jsts';
import { konvaSettings } from '../konvaSettings';
import { Utils } from '../../../core/services/utils.service';
import { Equipment } from '../equipment';
import { Bounding } from '../../../core/models/bounding';
import { Range } from '../../../core/models/range';
import { UnitConverter } from '../../../core/utils/unitConverter';
import { applyShapeFillParamsFromLoad, setShapeFillColor } from '../elementFillColor';

const Coordinate = jsts.geom.Coordinate,
	GeometryFactory = jsts.geom.GeometryFactory,
	geometryFactory: jsts.geom.GeometryFactory = new GeometryFactory();

export class Trapeze implements Figure {
	type: string = 'trapeze';
	begin: Point;
	height: number;
	depth: number;
	frontWidth: number;
	backWidth: number;
	wallWidth: number; // boxes
	priority: 'normal';
	color?: string;
	fillColor: string = konvaSettings.fillGreyColor;
	strokeColor: string = konvaSettings.fillDarkColor;
	empty: {
		front: boolean,   // відмальовка порожнистого обєкта
		top: boolean
	};
	prevObjectHash: string;
	representation: string = undefined;
	prev_usable_area_type: boolean;

	cache: any = Trapeze.returnClearCacheObject();

	get square(): number {
		return Utils.round((this.frontWidth + this.backWidth) / 2 * this.depth);
	}

	constructor(params: any) {
		this.wallWidth = UnitConverter.convertAndRoundToSaveValue(0.01);

		const {
				empty,
				begin,
				frontWidth,
				backWidth,
				depth,
				height,
				fillColor,
				priority,
				wallWidth
			} = params,
			validatedParams = this.validate({
				empty,
				begin,
				frontWidth,
				backWidth,
				depth,
				height,
				fillColor,
				priority,
				wallWidth
			});

		Object.assign(this, validatedParams);
		applyShapeFillParamsFromLoad(this, params);
	}

	set beginSetter(value: Point) {
		this.begin = {
			x: Utils.round(value.x),
			y: Utils.round(value.y),
			z: Utils.round(value.z),
		};

		this.cache = Trapeze.returnClearCacheObject();
	}

	get coordinates(): Array<Point> {
		const coords = this.getLineCordinates('front');

		return [
			...coords.map((c, idx) => ({
				x: c[0],
				y: c[1],
				z: (idx % 2) == 0 ? this.begin.z + this.depth : this.begin.z
			})),
			{ x: coords[0][0], y: coords[0][1], z: this.begin.z + this.depth }
		];
	}

	get coordinatesZ() {
		const coords = this.getLineCordinates('top');

		return [...coords.map(c => ({ x: c[0], y: c[1] })), { x: coords[0][0], y: coords[0][1] }];
	}

	get tan_a() {
		return (this.backWidth - this.frontWidth) / 2 / this.depth;
	}

	get savedParams() {
		const {
			empty,
			begin,
			frontWidth,
			backWidth,
			depth,
			height,
			color,
			fillColor,
			priority,
			wallWidth,
			type
		} = this;
		return {
			empty,
			begin,
			frontWidth,
			backWidth,
			depth: depth ?? 0,
			height: height ?? 0,
			color,
			fillColor,
			priority,
			wallWidth: wallWidth ?? UnitConverter.convertAndRoundToSaveValue(0.01),
			type
		};
	}

	static returnClearCacheObject() {
		return {
			front: {},
			top: {},
			side: {}
		};
	}

	private getCachedValue(params) {
		const { calculate_func, key, representation = 'front', usable_area = false } = params;

		if (!this.cache[representation]) {
			this.cache[representation] = {};
		}

		if (key == 'getBoundingX' && usable_area != this.prev_usable_area_type) {
			delete this.cache[representation][key];
		}

		if (this.prevObjectHash) {
			const { begin } = JSON.parse(this.prevObjectHash);

			if (!Utils.equalObjects(begin, this.begin)) {
				delete this.cache[representation][key];
			}
		}

		if (!this.cache[representation][key]) {
			if (typeof calculate_func == 'function') {
				this.cache[representation][key] = calculate_func();
			}
		}

		return this.cache[representation][key];
	}

	isEqual(hash: string): boolean {
		return this.prevObjectHash == hash;
	}

	getObjectHash(kwargs: any) {
		const {
			begin,
			depth,
			height,
			strokeColor,
			color,
			fillColor,
			type,
			wallWidth,
			backWidth,
			frontWidth,
			representation
		} = this;

		return JSON.stringify({
			begin,
			depth,
			height,
			strokeColor,
			color,
			fillColor,
			type,
			wallWidth,
			backWidth,
			frontWidth,
			representation,
			...kwargs
		});
	}

	update(params: any): void {
		Object.keys(params).forEach((param: string) => {
			if (this[param] != undefined && params[param] != null) this[param] = params[param];
		});

		const storedFill = params.color ?? params.fillColor;

		if (storedFill) {
			setShapeFillColor(this, storedFill);
		}

		this.cache = Trapeze.returnClearCacheObject();
	}

	copy(): Trapeze {
		const {
			empty,
			begin,
			frontWidth,
			backWidth,
			depth,
			height,
			color,
			fillColor,
			priority,
			wallWidth
		} = this;

		return new Trapeze({
			frontWidth, backWidth, depth, height, color, fillColor, priority, wallWidth,
			empty: Object.assign({}, empty),
			begin: Object.assign({}, begin)
		});
	}

	getBoundingX(usable_area: boolean = false): Bounding {
		return this.getCachedValue({
			key: 'getBoundingX',
			calculate_func: () => {
				this.prev_usable_area_type = usable_area;
				// костиль для розрахунку "корисної площі" трапеції (викор. на автовикладці)
				if (usable_area) {
					const output = {
						min: 0,
						max: 0
					};

					if (this.frontWidth >= this.backWidth) {
						output.min = Utils.round(this.begin.x + (this.frontWidth - this.backWidth) / 2);
					} else {
						output.min = Utils.round(this.begin.x + (this.backWidth - this.frontWidth) / 2);
					}

					output.max = Utils.round(this.begin.x + (this.backWidth + this.frontWidth) / 2);
					return output;
				}

				const xList = [this.begin.x, this.begin.x + this.backWidth, this.begin.x + this.frontWidth];

				return {
					min: Utils.round(Math.min(...xList)),
					max: Utils.round(Math.max(...xList))
				};
			},
			usable_area
		});
	};

	getBoundingY(): Bounding {
		return this.getCachedValue({
			key: 'getBoundingY',
			calculate_func: () => {
				const yList = [this.begin.y, this.begin.y + this.height];

				return {
					min: Utils.round(Math.min(...yList)),
					max: Utils.round(Math.max(...yList))
				};
			}
		});
	}

	getBoundingZ(): Bounding {
		return this.getCachedValue({
			key: 'getBoundingZ',
			calculate_func: () => {
				const zList = [this.begin.z, this.begin.z + this.depth];

				return {
					min: Utils.round(Math.min(...zList)),
					max: Utils.round(Math.max(...zList))
				};
			}
		});
	}

	getBoundingZBetween(compareSize: Bounding): Bounding {
		const bottom = this.getLineCordinates('top');
		const { min: thisMinZ, max: thisMaxZ } = this.getBoundingZ();
		const minXPolygon = geometryFactory.createLineString([
			new Coordinate(compareSize.min, thisMinZ),
			new Coordinate(compareSize.min, thisMaxZ),
		]);
		const maxXPolygon = geometryFactory.createLineString([
			new Coordinate(compareSize.max, thisMinZ),
			new Coordinate(compareSize.max, thisMaxZ),
		]);
		const thisPolygon = geometryFactory.createPolygon([
				new Coordinate(bottom[0][0], bottom[0][1]),
				new Coordinate(bottom[1][0], bottom[1][1]),
				new Coordinate(bottom[2][0], bottom[2][1]),
				new Coordinate(bottom[3][0], bottom[3][1]),
				new Coordinate(bottom[0][0], bottom[0][1])
			]);

		if (!thisPolygon.intersects(minXPolygon) || !thisPolygon.intersects(maxXPolygon)) {
			return {
				min: 0,
				max: 0
			};
		}

		try {	
			const [minXStart, minXEnd] = minXPolygon.intersection(thisPolygon).getCoordinates().map(({ y }) => y).sort((a, b) => a - b);
			const [maxXStart, maxXEnd] = maxXPolygon.intersection(thisPolygon).getCoordinates().map(({ y }) => y).sort((a, b) => a - b);
			
			return {
				min: Utils.round(Math.max(minXStart ?? -Infinity, maxXStart ?? -Infinity)),
				max: Utils.round(Math.min(minXEnd ?? Infinity, maxXEnd ?? Infinity))
			}
		} catch (error) {
			console.error(error);

			return {
				min: 0,
				max: 0
			};
		}
	}

	getSidesAngle() {
		const coordinates = this.getLineCordinates('top'),
			left = 180 - Math.atan2(
				coordinates[0][0] - coordinates[3][0],
				coordinates[0][1] - coordinates[3][1]) * 180 / Math.PI,

			right = 180 - Math.atan2(
				coordinates[1][0] - coordinates[2][0],
				coordinates[1][1] - coordinates[2][1]) * 180 / Math.PI;

		return { left, right };
	}

	isPointOn(x?: number, y?: number, z?: number) {
		if (x != null) {
			const xBounding = this.getBoundingX();
			x = Utils.round(x);
			if (x < xBounding.min || x > xBounding.max) return false;
		}
		if (y != null) {
			const yBounding = this.getBoundingY();
			y = Utils.round(y);
			if (y < yBounding.min || y > yBounding.max) return false;
		}
		if (z != null) {
			const zBounding = this.getBoundingZ();
			z = Utils.round(z);
			if (z < zBounding.min || z > zBounding.max) return false;
		}
		if (z != null && x != null) {
			const coordinates = this.getLineCordinates('top');
			coordinates.push(coordinates[0]);
			const area = geometryFactory.createPolygon(coordinates.map(c => new Coordinate(c[0], c[1])));
			area.contains(geometryFactory.createPoint(new Coordinate(x, z)));
		}

		return true;
	}

	drag(x?: number, y?: number, z?: number): void {
		if (x) this.begin.x = Utils.round(this.begin.x + x);
		if (y) this.begin.y = Utils.round(this.begin.y + y);
		if (z) this.begin.z = Utils.round(this.begin.z + z);

		this.cache = Trapeze.returnClearCacheObject();
	}

	getLineCordinates(representation: string) {
		return this.getCachedValue({
			key: 'getLineCordinates',
			calculate_func: () => {
				switch (representation) {
					case 'front': {
						return [
							[this.begin.x, Utils.round(this.begin.y + this.height)],
							[Utils.round(this.begin.x + Math.max(this.frontWidth, this.backWidth)), Utils.round(this.begin.y + this.height)],
							[Utils.round(this.begin.x + Math.max(this.frontWidth, this.backWidth)), this.begin.y],
							[this.begin.x, this.begin.y]
						];
					}
					case 'top': {
						if (this.frontWidth >= this.backWidth) {
							return [
								[Utils.round(this.begin.x + (this.frontWidth - this.backWidth) / 2), this.begin.z],
								[Utils.round(this.begin.x + (this.frontWidth + this.backWidth) / 2), this.begin.z],
								[Utils.round(this.begin.x + this.frontWidth), Utils.round(this.begin.z + this.depth)],
								[this.begin.x, Utils.round(this.begin.z + this.depth)]
							];
						} else {
							return [
								[this.begin.x, this.begin.z],
								[Utils.round(this.begin.x + this.backWidth), this.begin.z],
								[Utils.round(this.begin.x + (this.backWidth + this.frontWidth) / 2), Utils.round(this.begin.z + this.depth)],
								[Utils.round(this.begin.x + (this.backWidth - this.frontWidth) / 2), Utils.round(this.begin.z + this.depth)]
							];
						}
					}
				}
			},
			representation
		});
	}

	getLines(representation: string, instrument: Equipment): Array<Array<number>> {
		return this.getCachedValue({
			key: 'getLines',
			calculate_func: () => {
				const coordinates = this.getLineCordinates(representation),
					lines = [];

				for (let idx = 0; idx < coordinates.length; idx++) {
					if (idx != coordinates.length - 1) {
						lines.push([...coordinates[idx], ...coordinates[idx + 1]]);
					} else {
						lines.push([...coordinates[idx], ...coordinates[0]]);
					}
				}

				switch (representation) {
					case 'front': {
						const h = instrument.Height;

						return lines.map(c => [c[0], h - c[1], c[2], h - c[3]]);
					}
					case 'top': {
						return lines;
					}
				}
			},
			representation
		});
	}

	inRange(range: Range): boolean {
		const xBounding: Bounding = this.getBoundingX();

		return !(range.to <= xBounding.min || range.from >= xBounding.max);
	}

	private validate(params: any) {
		const validatedParams = {};

		for (let key in params) {
			if (params[key] != null) {
				validatedParams[key] = params[key];
			}
		}

		return validatedParams;
	}
}